/**
 * Draft selection for Feel / Style / Flavor.
 *
 * Styles and flavors are unordered sets. Canonical form sorts by the fixed
 * definition order so selection order cannot leak into saved state.
 * Nothing here writes to the crowd.
 */

import {
  FEEL_BY_ID,
  FEEL_IDS,
  FLAVOR_BY_ID,
  FLAVOR_IDS,
  MAX_STYLES,
  STYLE_BY_ID,
  STYLE_IDS,
  styleLimitMessage,
} from './definitions';

export function emptyVibeDraft() {
  return {
    feelId: null,
    styleIds: [],
    flavorIds: [],
  };
}

function knownIdsInDefinitionOrder(ids, allowedIds, byId) {
  if (!Array.isArray(ids)) return [];
  const seen = new Set();
  ids.forEach((id) => {
    if (typeof id === 'string' && byId[id]) seen.add(id);
  });
  return allowedIds.filter((id) => seen.has(id));
}

export function canonicalizeVibeSelection(input) {
  const feelId = typeof input?.feelId === 'string' && FEEL_BY_ID[input.feelId]
    ? input.feelId
    : null;
  return {
    feelId,
    styleIds: knownIdsInDefinitionOrder(input?.styleIds, STYLE_IDS, STYLE_BY_ID).slice(0, MAX_STYLES),
    flavorIds: knownIdsInDefinitionOrder(input?.flavorIds, FLAVOR_IDS, FLAVOR_BY_ID),
  };
}

/** A submission needs exactly one known Feel. Style and Flavor may be empty. */
export function isCompleteVibeSelection(selection) {
  if (!selection || !FEEL_BY_ID[selection.feelId]) return false;
  if (!Array.isArray(selection.styleIds) || selection.styleIds.length > MAX_STYLES) return false;
  if (!Array.isArray(selection.flavorIds)) return false;
  const styles = new Set(selection.styleIds);
  const flavors = new Set(selection.flavorIds);
  if (styles.size !== selection.styleIds.length) return false;
  if (flavors.size !== selection.flavorIds.length) return false;
  if (selection.styleIds.some((id) => !STYLE_BY_ID[id])) return false;
  if (selection.flavorIds.some((id) => !FLAVOR_BY_ID[id])) return false;
  return true;
}

export function selectFeel(draft, feelId) {
  const base = draft || emptyVibeDraft();
  if (!FEEL_BY_ID[feelId]) return { ...base, styleIds: [...(base.styleIds || [])], flavorIds: [...(base.flavorIds || [])] };
  return {
    feelId,
    styleIds: [...(base.styleIds || [])],
    flavorIds: [...(base.flavorIds || [])],
  };
}

/**
 * Selecting a sixth style does not drop an older one and does not reorder.
 * Returns the same style ids when blocked.
 */
export function toggleStyle(draft, styleId) {
  const current = canonicalizeVibeSelection(draft);
  if (!STYLE_BY_ID[styleId]) {
    return { draft: current, blocked: false, message: '' };
  }
  if (current.styleIds.includes(styleId)) {
    return {
      draft: {
        ...current,
        styleIds: current.styleIds.filter((id) => id !== styleId),
      },
      blocked: false,
      message: '',
    };
  }
  if (current.styleIds.length >= MAX_STYLES) {
    return {
      draft: current,
      blocked: true,
      message: styleLimitMessage(),
    };
  }
  return {
    draft: canonicalizeVibeSelection({
      ...current,
      styleIds: [...current.styleIds, styleId],
    }),
    blocked: false,
    message: '',
  };
}

export function toggleFlavor(draft, flavorId) {
  const current = canonicalizeVibeSelection(draft);
  if (!FLAVOR_BY_ID[flavorId]) return current;
  if (current.flavorIds.includes(flavorId)) {
    return {
      ...current,
      flavorIds: current.flavorIds.filter((id) => id !== flavorId),
    };
  }
  return canonicalizeVibeSelection({
    ...current,
    flavorIds: [...current.flavorIds, flavorId],
  });
}

export function preferenceToDraft(preference) {
  if (!preference) return emptyVibeDraft();
  return canonicalizeVibeSelection(preference);
}

export function vibeSelectionsEqual(left, right) {
  const a = canonicalizeVibeSelection(left);
  const b = canonicalizeVibeSelection(right);
  return a.feelId === b.feelId
    && a.styleIds.join('|') === b.styleIds.join('|')
    && a.flavorIds.join('|') === b.flavorIds.join('|');
}

export { FEEL_IDS };
